/**
 * TokenPicker — token selector for the Transfer screen.
 *
 * Shows ONLY tokens with a balance > 0 (you can't send what you don't have),
 * each with its logo, name, symbol, contract, balance and USD value.
 *
 * Renders a compact trigger row; tapping it opens a bottom-sheet list.
 */

import { useMemo, useState } from 'react'
import type { TokenBalance } from '@properwallet/chain-eos'
import { formatUsd } from '@properwallet/chain-eos'
import { useTranslation } from '../hooks/useTranslation'
import InfoTip from './InfoTip'

export function tokenKeyOf(t: TokenBalance): string {
  return `${t.contract}::${t.symbol}`
}

// ─── Logo with letter fallback ────────────────────────────────────────────────

function TokenLogo({ token, size = 32 }: { token: TokenBalance; size?: number }) {
  const [failed, setFailed] = useState(false)
  const isVaulta = token.symbol === 'A'
  const isEos = token.symbol === 'EOS'
  const gradient = isVaulta
    ? 'from-brand to-brand-dark'
    : isEos
      ? 'from-blue-500 to-cyan-600'
      : 'from-gray-500 to-gray-600'

  if (token.logoUrl && !failed) {
    return (
      <img
        src={token.logoUrl}
        alt=""
        width={size}
        height={size}
        className="rounded-full object-cover bg-surface-2 flex-shrink-0"
        style={{ width: size, height: size }}
        onError={() => setFailed(true)}
      />
    )
  }

  return (
    <div
      className={`rounded-full bg-gradient-to-br ${gradient} flex items-center justify-center flex-shrink-0`}
      style={{ width: size, height: size }}
    >
      <span className="text-white font-bold" style={{ fontSize: size * 0.42 }}>
        {token.symbol.charAt(0).toUpperCase()}
      </span>
    </div>
  )
}

// ─── Picker ───────────────────────────────────────────────────────────────────

interface TokenPickerProps {
  tokens: TokenBalance[]
  selected: TokenBalance | null
  onSelect: (token: TokenBalance) => void
}

export default function TokenPicker({ tokens, selected, onSelect }: TokenPickerProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')

  const showSearch = tokens.length > 6

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return tokens
    return tokens.filter(
      tok =>
        tok.symbol.toLowerCase().includes(q) ||
        tok.name.toLowerCase().includes(q) ||
        tok.contract.toLowerCase().includes(q),
    )
  }, [tokens, query])

  const close = () => {
    setOpen(false)
    setQuery('')
  }

  return (
    <>
      {/* Trigger */}
      <button
        type="button"
        onClick={() => tokens.length > 0 && setOpen(true)}
        disabled={tokens.length === 0}
        className="w-full card flex items-center gap-3 text-left disabled:opacity-50"
        style={{ cursor: tokens.length === 0 ? 'not-allowed' : 'pointer' }}
      >
        {selected ? (
          <>
            <TokenLogo token={selected} size={36} />
            <div className="flex-1 min-w-0">
              <p className="text-white font-semibold text-sm truncate">
                {selected.symbol}
                <span className="text-gray-500 font-normal"> · {selected.name}</span>
              </p>
              <p className="text-xs text-gray-500 truncate">{selected.contract}</p>
            </div>
            <div className="text-right flex-shrink-0">
              <p className="text-white text-sm font-medium">
                {parseFloat(selected.amount).toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: selected.precision,
                })}
              </p>
              {selected.usdValue !== undefined && selected.usdValue > 0 && (
                <p className="text-xs text-green-400/80">{formatUsd(selected.usdValue)}</p>
              )}
            </div>
          </>
        ) : (
          <span className="flex-1 text-sm text-gray-400">
            {tokens.length === 0 ? t('transfer.noTokens') : t('transfer.selectToken')}
          </span>
        )}
        <span className="text-gray-500 text-xs flex-shrink-0">▼</span>
      </button>

      {/* Bottom sheet */}
      {open && (
        <div
          className="fixed inset-0 z-50 flex flex-col justify-end"
          style={{ background: 'rgba(0,0,0,0.7)' }}
          onClick={close}
        >
          <div
            className="rounded-t-2xl flex flex-col"
            style={{
              background: '#0c100c',
              borderTop: '1px solid rgba(255,255,255,0.08)',
              maxHeight: '75vh',
            }}
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 pt-4 pb-2 flex-shrink-0">
              <h3 className="text-white font-semibold text-base">{t('transfer.selectToken')}</h3>
              <button
                type="button"
                onClick={close}
                className="text-gray-400 text-xl leading-none px-2"
                style={{ background: 'none', border: 'none' }}
              >
                ×
              </button>
            </div>

            {showSearch && (
              <div className="px-4 pb-2 flex-shrink-0">
                <input
                  type="text"
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  placeholder={t('transfer.searchToken')}
                  className="input-field"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                />
              </div>
            )}

            <div className="scroll-area px-3 pb-6 space-y-1">
              {filtered.map(tok => {
                const key = tokenKeyOf(tok)
                const isActive = selected != null && tokenKeyOf(selected) === key
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => {
                      onSelect(tok)
                      close()
                    }}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-colors ${
                      isActive ? 'bg-brand/10 border border-brand/40' : 'border border-transparent'
                    }`}
                    style={{ background: isActive ? undefined : 'rgba(255,255,255,0.02)' }}
                  >
                    <TokenLogo token={tok} size={32} />
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-medium truncate">
                        {tok.symbol}
                        {!tok.isKnown && (
                          <InfoTip text={t('token.unknownWarning')} />
                        )}
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        {tok.name} · {tok.contract}
                      </p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-white text-sm">
                        {parseFloat(tok.amount).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: tok.precision,
                        })}
                      </p>
                      {tok.usdValue !== undefined && tok.usdValue > 0 && (
                        <p className="text-xs text-green-400/80">{formatUsd(tok.usdValue)}</p>
                      )}
                    </div>
                  </button>
                )
              })}

              {filtered.length === 0 && (
                <p className="text-center text-sm text-gray-500 py-8">
                  {t('transfer.noTokens')}
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  )
}
